import React, { useState } from 'react';
import Card from '../../../../shared/components/Card';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

export interface TrainingWithParticipants {
  id: string;
  programme: string;
  remainingTime: string;
  user: string;
  isAlliance?: boolean;
}

export interface OpenTraining {
  id: string;
  programme: string;
  freeSlots: number;
  costPerDayPerPerson: number;
  remainingTime: string;
  user: string;
}

const MOCK_WITH_PARTICIPANTS: TrainingWithParticipants[] = [
  { id: '1', programme: "Secours nautique - Sauvetage et déblaiement", remainingTime: "3 j 20:35:42", user: "Global Rescue", isAlliance: true },
  { id: '2', programme: "Secours nautique - Formation Pilote d'Intervention", remainingTime: "3 j 05:34:59", user: "Matteospv", isAlliance: false },
];

const MOCK_OPEN: OpenTraining[] = [
  { id: '1', programme: "Secours nautique - Accréditation nautique", freeSlots: 6, costPerDayPerPerson: 0, remainingTime: "3 j 15:01:08", user: "Global Rescue" },
  { id: '2', programme: "Secours nautique - Initiation", freeSlots: 4, costPerDayPerPerson: 50, remainingTime: "5 j 08:00:00", user: "Global Rescue" },
];

const AllianceFormations: React.FC = () => {
  const { t } = usePageTranslation('game', 'AlliancePage');
  const [searchOpen, setSearchOpen] = useState('');

  const filteredOpen = MOCK_OPEN.filter(
    (row) =>
      row.programme.toLowerCase().includes(searchOpen.toLowerCase()) ||
      row.user.toLowerCase().includes(searchOpen.toLowerCase())
  );

  return (
    <Card className="p-6">
      <h2 className="text-2xl font-bold text-white mb-6">{t('training.title')}</h2>

      <section className="mb-10">
        <h3 className="text-lg font-semibold text-gray-300 mb-4">{t('training.withParticipants')}</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-800/80 border-b border-gray-700">
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('training.colProgramme')}</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('training.colRemainingTime')}</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('training.colUser')}</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_WITH_PARTICIPANTS.map((row) => (
                <tr key={row.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded-lg bg-green-600/80 hover:bg-green-600 text-white text-sm font-medium"
                    >
                      {row.programme}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-400 tabular-nums">{row.remainingTime}</td>
                  <td className="px-4 py-3">
                    {row.isAlliance ? (
                      <span className="text-gray-300">{row.user}</span>
                    ) : (
                      <button type="button" className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                        </svg>
                        {row.user}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-300 mb-4">{t('training.open')}</h3>
        <div className="mb-4 flex justify-end">
          <div className="relative">
            <input
              type="text"
              value={searchOpen}
              onChange={(e) => setSearchOpen(e.target.value)}
              placeholder={t('training.search')}
              className="pl-4 pr-10 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 text-sm w-48 focus:ring-2 focus:ring-indigo-500"
            />
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-800/80 border-b border-gray-700">
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('training.colProgramme')}</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('training.colFreeSlots')}</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('training.colCost')}</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('training.colRemainingTime')}</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('training.colUser')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOpen.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    {t('training.noResults')}
                  </td>
                </tr>
              ) : (
                filteredOpen.map((row) => (
                  <tr key={row.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="px-3 py-1.5 rounded-lg bg-green-600/80 hover:bg-green-600 text-white text-sm font-medium"
                      >
                        {row.programme}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-400 tabular-nums">{row.freeSlots}</td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {row.costPerDayPerPerson === 0
                        ? t('training.free')
                        : `${row.costPerDayPerPerson.toLocaleString('fr-FR')} € ${t('training.perDayPerPerson')}`}
                    </td>
                    <td className="px-4 py-3 text-gray-400 tabular-nums">{row.remainingTime}</td>
                    <td className="px-4 py-3 text-gray-300">{row.user}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </Card>
  );
};

export default AllianceFormations;
